import type { CategorizedHPOOption } from '@/types/RequisitionFormTypes'
import { useEffect, useMemo, useState } from 'react'

/** Per-term decision: keep the term in the indications, or drop it. */
type Decision = 'keep' | 'remove'

/** Which reconciliation section a row belongs to. */
type Section = 'added' | 'removed'

/**
 * Default decision for a row when the provider has not toggled it. Defaults
 * match the section intent so a plain Confirm applies the diff as shown: newly
 * added terms are kept (added) and terms the summary no longer supports are
 * dropped (removed). Providers can override either direction per row.
 */
function defaultDecisionFor(section: Section): Decision {
  return section === 'removed' ? 'remove' : 'keep'
}

export interface PhenotypeReconciliationDecisions {
  /** Proposed additions the provider chose to keep (will be added). */
  additionsToKeep: CategorizedHPOOption[]
  /** Terms the provider chose to drop (added-but-rejected, or drifted-and-removed). */
  termsToRemove: CategorizedHPOOption[]
}

interface EditFlowPhenotypeReviewBannerProps {
  /** Terms the latest re-extraction newly surfaced from the amended summary. */
  addedTerms: CategorizedHPOOption[]
  /** Auto-extracted terms the amended summary no longer supports. */
  removedTerms: CategorizedHPOOption[]
  /** Apply every staged keep/remove decision at once. */
  onConfirm: (decisions: PhenotypeReconciliationDecisions) => void
}

const BANNER_INSTRUCTION =
  'Review phenotype changes from the edited clinical summary before sending updates. Click Confirm to accept the selected Keep/Remove choices.'

function signatureOf(added: CategorizedHPOOption[], removed: CategorizedHPOOption[]): string {
  return [...added.map((t) => `a:${t.id}`), ...removed.map((t) => `r:${t.id}`)].join('|')
}

/**
 * Edit-order flow phenotype reconciliation banner (GEN-3467).
 *
 * After re-extracting phenotypes against an amended clinical summary, this
 * single banner surfaces both directions of change in two sections: terms the
 * edit newly added (top) and previously auto-extracted terms the edit no longer
 * supports (bottom). Every term carries its own keep/remove choice; selections
 * are staged and visible but nothing is applied until the provider clicks
 * Confirm. Defaults match each section's intent (added terms default to "keep",
 * dropped terms default to "remove"), so a plain Confirm applies the diff as
 * shown. Providers can override any row in either direction.
 *
 * Manually curated terms are never listed here (the caller filters drift to
 * auto-extracted terms and additions to genuinely new codes).
 */
export function EditFlowPhenotypeReviewBanner({
  addedTerms,
  removedTerms,
  onConfirm,
}: EditFlowPhenotypeReviewBannerProps) {
  const signature = useMemo(() => signatureOf(addedTerms, removedTerms), [addedTerms, removedTerms])

  // Per-term decisions, keyed by term id and defaulting to 'keep'. When the
  // proposed term set changes, prune only the entries for terms that are gone
  // and preserve the provider's choices for terms that remain. Clearing every
  // choice here would silently reset selections whenever the parent's filtered
  // lists shrink (e.g. the provider manually adds a pending term or re-affirms a
  // drift term mid-review), losing any 'remove' they had picked.
  const [decisions, setDecisions] = useState<Record<string, Decision>>({})
  useEffect(() => {
    const validIds = new Set([...addedTerms, ...removedTerms].map((term) => term.id))
    setDecisions((prev) => {
      const next: Record<string, Decision> = {}
      let pruned = false
      for (const [id, decision] of Object.entries(prev)) {
        if (validIds.has(id)) {
          next[id] = decision
        } else {
          pruned = true
        }
      }
      return pruned ? next : prev
    })
    // `signature` encodes the current term ids; addedTerms/removedTerms are read
    // via closure and are consistent with it on the render that changed it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signature])

  const decisionFor = (id: string, section: Section): Decision => decisions[id] ?? defaultDecisionFor(section)
  const setDecision = (id: string, decision: Decision) => {
    setDecisions((prev) => ({ ...prev, [id]: decision }))
  }

  if (addedTerms.length === 0 && removedTerms.length === 0) {
    return null
  }

  const handleConfirm = () => {
    const additionsToKeep = addedTerms.filter((term) => decisionFor(term.id, 'added') === 'keep')
    const termsToRemove = [
      ...addedTerms.filter((term) => decisionFor(term.id, 'added') === 'remove'),
      ...removedTerms.filter((term) => decisionFor(term.id, 'removed') === 'remove'),
    ]
    onConfirm({ additionsToKeep, termsToRemove })
  }

  const renderRow = (term: CategorizedHPOOption, section: Section) => {
    const decision = decisionFor(term.id, section)
    return (
      <li
        key={`${section}-${term.id}`}
        className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-border-base bg-white px-3 py-2 text-xs leading-5 text-text-body shadow-xs sm:text-sm dark:border-gray-700 dark:bg-gray-900"
      >
        <span className="min-w-0 flex-1 truncate">
          <span className="font-medium">{term.label || term.id}</span>
          {term.label && term.label !== term.id ? <span className="ml-1 text-text-muted">({term.id})</span> : null}
        </span>
        <span className="flex shrink-0 items-center gap-2" role="group" aria-label={`Keep or remove ${term.label || term.id}`}>
          <button
            type="button"
            aria-pressed={decision === 'keep'}
            onClick={() => setDecision(term.id, 'keep')}
            className={`rounded-md border px-2 py-1 text-xs font-medium transition-colors ${
              decision === 'keep'
                ? 'border-border-brand-subtle bg-brand-100 text-text-fg-info'
                : 'border-border-base text-text-body hover:bg-gray-50'
            }`}
            data-testid={`edit-flow-phenotype-review-keep-${section}-${term.id}`}
          >
            Keep
          </button>
          <button
            type="button"
            aria-pressed={decision === 'remove'}
            onClick={() => setDecision(term.id, 'remove')}
            className={`rounded-md border px-2 py-1 text-xs font-medium transition-colors ${
              decision === 'remove'
                ? 'border-red-300 bg-red-50 text-red-600'
                : 'border-border-base text-text-body hover:bg-red-50'
            }`}
            data-testid={`edit-flow-phenotype-review-remove-${section}-${term.id}`}
          >
            Remove
          </button>
        </span>
      </li>
    )
  }

  return (
    <div
      className="flex flex-col gap-4 rounded-base border border-border-brand-subtle bg-background-brand-softer px-3 pb-3 pt-4 sm:px-4 sm:pb-4 sm:pt-5"
      data-testid="edit-flow-phenotype-review-banner"
    >
      <div className="flex items-start gap-2">
        <span className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-brand-100 text-text-fg-info">
          i
        </span>
        <p className="flex-1 text-xs font-medium leading-5 text-text-fg-info sm:text-sm">{BANNER_INSTRUCTION}</p>
      </div>

      {addedTerms.length > 0 && (
        <div
          className="ml-6 flex flex-col gap-2 rounded-md border border-green-200 border-l-4 border-l-green-500 bg-green-50 px-3 py-2.5 dark:border-green-900 dark:border-l-green-500 dark:bg-green-950/30"
          data-testid="edit-flow-phenotype-review-added"
        >
          <span className="inline-flex w-fit items-center rounded-full bg-green-100 px-2 py-0.5 text-xs font-semibold uppercase tracking-wide text-green-700 dark:bg-green-900 dark:text-green-300">
            Added
          </span>
          <ul className="flex flex-col gap-2">{addedTerms.map((term) => renderRow(term, 'added'))}</ul>
        </div>
      )}

      {removedTerms.length > 0 && (
        <div
          className="ml-6 flex flex-col gap-2 rounded-md border border-red-200 border-l-4 border-l-red-500 bg-red-50 px-3 py-2.5 dark:border-red-900 dark:border-l-red-500 dark:bg-red-950/30"
          data-testid="edit-flow-phenotype-review-removed"
        >
          <span className="inline-flex w-fit items-center rounded-full bg-red-100 px-2 py-0.5 text-xs font-semibold uppercase tracking-wide text-red-700 dark:bg-red-900 dark:text-red-300">
            No longer in summary
          </span>
          <ul className="flex flex-col gap-2">{removedTerms.map((term) => renderRow(term, 'removed'))}</ul>
        </div>
      )}

      <div className="ml-6 flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={handleConfirm}
          className="rounded-full bg-brand-600 px-3 py-1.5 text-xs font-medium text-white transition-colors hover:bg-brand-700 sm:text-sm"
          data-testid="edit-flow-phenotype-review-confirm"
        >
          Confirm
        </button>
      </div>
    </div>
  )
}
